'use client'
import { SubmitButton } from '../submit-button';
import { QuestionCard } from '../question-card';
import { ErrorMsg } from '../error-msg';
import { SurveyFormTextField } from '../survey-form-textfield';
import { RecommendCheckList } from '../recommend-checklist';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { StarRatingContainer } from '../star-rating-container';
import { useForm, FormProvider, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PlayerSurveyFormSchema, PlayerSurveyDataInputs } from '@/lib/schemas/player-survey.schema';
import { savePlayerSurvey, usePlayerSurveyIsSubmitted } from '@/hooks/query/use-player-survey';
import { authService } from '@/services/auth.service';
import smoothscroll from 'smoothscroll-polyfill';

interface SurveyFormProps {
	setShowSurveyForm: (showSurveyForm: boolean) => void;
	setFeedbackRecord: (feedbackRecord: string) => void;
}

export const SurveyForm = ({ setShowSurveyForm, setFeedbackRecord }:SurveyFormProps) => {
	const t = useTranslations('General');
	const { refetch, isSubmitted } = usePlayerSurveyIsSubmitted();
	const defaultValues ={
		comment: '',
		recommendScore: 0,
		recommendOptions: '',
		otherRecommendComment: '',
		loveScore: 0,
	}
	const prevData = localStorage.getItem('player-survey-data');
	const defaultFormData = prevData ? JSON.parse(prevData): defaultValues;
	
	const methods = useForm({
		resolver: zodResolver(PlayerSurveyFormSchema),
		values: defaultFormData,
		shouldFocusError: false 
	});
	
	const { watch, register, handleSubmit, control, reset, getValues, setValue, setFocus, setError, clearErrors, formState: { errors } } = methods;	
	const [recommendType, setRecommendType] = useState(watch('recommendScore') > 3);
	const [recommendScore, setRecommendScore] = useState(watch('recommendScore'));
	const [submitFormLoading, setSubmitFormLoading] = useState(false);
	// submit error msg
	const [submitErrorMsg, setSubmitErrorMsg] = useState('');
	const watchAllFields = watch();

	// set feedback record for save localstorage
	useEffect(() => {
		setFeedbackRecord(JSON.stringify(watchAllFields))
	}, [watchAllFields])

	// scroll to error field
	useEffect(() => {
		smoothscroll.polyfill();		
		const elements = Object.keys(errors)
			.map(name => document.getElementsByName(name)[0]?.closest('.survey-form__item'))
			.filter(el => el);

		elements.sort((a, b) => {
			const aTop = a ? a.getBoundingClientRect().top : 0;
			const bTop = b ? b.getBoundingClientRect().top : 0;
			return aTop - bTop;
		});
		elements[0]?.scrollIntoView({ 
			behavior: 'smooth', 
			block: 'center' 
		});
	}, [errors]);

	useEffect(() => {
		if (!!submitErrorMsg) {
			scrollToFormBottom()
		}
	}, [submitErrorMsg])

	// scroll to bottom 
	const scrollToFormBottom = () => {
		const element = document.getElementsByClassName('survey-form__submit')[0];
		element.scrollIntoView({ 
			behavior: 'smooth', 
			block: 'center' 
		});
	}

	const onRatingRecommend = (rating: number) => {
		if (rating ===0) {
			setValue('recommendOptions', '', { shouldValidate: true });
			setValue('otherRecommendComment', '', { shouldValidate: true });
		}
		setRecommendType(rating> 3) 
		setRecommendScore(rating)
	}		

	const isOverOneMinute = () => {
		if (!localStorage.getItem('player-survey-submit-time')) return true;
		const currentTime = new Date().getTime();
		const lastSubmitTime = Number(localStorage.getItem('player-survey-submit-time'));
	
		return (currentTime - lastSubmitTime) > 60000;
	}

	// submit form
	const submitFormData: SubmitHandler<PlayerSurveyDataInputs> = formData => {		
		setSubmitFormLoading(true)
		if (!isOverOneMinute()) {
			setSubmitErrorMsg('lbl_SabaSurveyFrequency') 
			setSubmitFormLoading(false)
			return;
		}
		
		savePlayerSurvey(formData).then(res => {
			if (res.data.Success) {
				localStorage.setItem('player-survey-submit-time', new Date().getTime().toString())
				if (!isSubmitted) {
					refetch()
				}
				setSubmitFormLoading(false)
				setSubmitErrorMsg('')
				reset(defaultValues)
				setShowSurveyForm(false)
				document.dispatchEvent(new CustomEvent('show-toast-top-msg', {
					detail: { resourceKey: 'lbl_SabaSurveySubmit01' }
				}));
				localStorage.removeItem('player-survey-data');
			} else {
				setSubmitFormLoading(false)
				setSubmitErrorMsg('lbl_SabaSurveyErrorMessage01')
			}
			if (res.jwtToken) {
				authService.updateJwtToken(res.jwtToken);
			}
		})
	}
	return (
		<div className="survey-form" >
			<FormProvider {...methods}>
				<QuestionCard
					no={1}
					title={t('lbl_GSabaSurveyQ01')} 
					description={null}
				>
					
					<div className="survey-form__content" data-error={!!errors.recommendScore}>
						<div className="survey-rank">
							<div className="survey-rank__legend">
								<small>{t('lbl_GSabaSurveyRecomend01')}</small>
								<small>{t('lbl_GSabaSurveyRecomend02')}</small>
							</div>
							<Controller 
								name="recommendScore"
								control={control}
								render={({ field }) => (
									<StarRatingContainer 
										field={field} 
										amount={5}
										iconType="hand"
										onRating={onRatingRecommend}
									/>
								)}
							/>
						</div>
						{errors.recommendScore?.message &&
						<ErrorMsg 
							msg={errors.recommendScore.message} 
						/>}
					</div>
				</QuestionCard>

				{recommendScore > 0 ? 
					<QuestionCard
						no={2}
						title={recommendType ? t('lbl_GSabaSurveyQuestion1') : t('lbl_GSabaSurveyQuestion2')}
						description={null}
					>
						<Controller 
							name="recommendOptions"
							control={control}
							render={({ field }) => (
								<RecommendCheckList 
									register={register}
									field={field}
									error={errors}
									reset={reset}
									setError={setError}
									clearErrors={clearErrors}
									recommendScore={recommendScore}
									recommendType={recommendType}
									getValues={getValues}
									setFocus={setFocus}
								/>
							)}
						/>
					</QuestionCard> 
					: null}

				<QuestionCard
					no={3}
					title={t('lbl_GSabaSurveyQuestion3')}
					description={null}
				>
					<div className="survey-form__content" data-error={!!errors.loveScore}>
						<div className="survey-rank">
							<div className="survey-rank__legend">
							</div>
							<Controller 
								name="loveScore"
								control={control}
								render={({ field }) => (
									<StarRatingContainer 
										field={field} 
										amount={5}
										iconType="star"
									/>
								)}
							/>
							{errors.loveScore?.message &&
							<ErrorMsg 
								msg={errors.loveScore.message} 
							/>}
						</div>
					</div>
				</QuestionCard>
						
				<QuestionCard 
					no={4}
					title={t('lbl_GSabaSurveyProblem')}
					description={t('lbl_GSabaSurveyExample')}
				>	
					<SurveyFormTextField
						register={register}
						error={errors.comment}
					/>
				</QuestionCard>	
			</FormProvider>

			<SubmitButton 
				onSubmit={handleSubmit(submitFormData)} 
				isLoading={submitFormLoading}
				errorMsg={submitErrorMsg}
			/>
		</div>
	)
}